/**
 * Server-side consent log: BackendBot MR !3123 (branch feature/gdpr-ccpa-privacy,
 * django_app/privacy/consent_api.py `ConsentEventView`, serializers.py
 * `ConsentEventSerializer`). Fire-and-forget, never throws, never blocks the UI,
 * never turns a failure into consent.
 *
 *   GET  /api/v1/privacy/consent   (?consent_id= or X-Consent-Id; or the signed-in user)
 *        -> 200 { current: null | { id, consent_id, categories, action,
 *                                   consent_version, policy_version, received_at } }
 *        Also sets the csrftoken cookie (ensure_csrf_cookie).
 *   POST /api/v1/privacy/consent   AllowAny (anonymous OK), CSRF enforced for every caller
 *        body = ConsentApiPayload below, exactly the serializer fields
 *        -> 201 { id, received_at } | 400 field errors (never retried)
 *        | 429 (30/min anon, 60/min user) + Retry-After (respected)
 *        | 403/404/405/5xx/network -> queued, exponential backoff, max 6 attempts
 *   Serializer rules: categories {necessary:true, analytics, marketing} strict
 *   booleans, no extra keys; accept_all => both true; reject_all/withdraw =>
 *   both false; page = path only; locale BCP-47-ish; gpc strict bool; with GPC
 *   marketing is stored false. The server derives sale_share/targeted_ads.
 *
 * Anonymous visitors: the choice is kept in localStorage/cookie and also posted
 * anonymously (the endpoint allows it) under consent_id. After login,
 * syncConsentAfterLogin() reads the server state and merges it (see below).
 */
import { getConfig, PENDING_KEY } from './config'
import {
  applyServerConsent,
  getConsent,
  getConsentId,
  readStoredConsent,
  type ConsentAction,
  type StoredConsent,
} from './consent-store'

export interface ConsentApiPayload {
  consent_id: string
  categories: { necessary: true; analytics: boolean; marketing: boolean }
  action: ConsentAction
  consent_version: number
  policy_version: string
  timestamp: string
  source: StoredConsent['source']
  page: string
  locale: string
  gpc: boolean
}

const LOCALE_RE = /^[A-Za-z]{2,3}([-_][A-Za-z0-9]{2,8})*$/

export function currentLocale(): string {
  const cands = [
    typeof document !== 'undefined' ? document.documentElement.lang : '',
    typeof navigator !== 'undefined' ? navigator.language : '',
  ]
  for (const c of cands) if (c && LOCALE_RE.test(c)) return c
  return 'en'
}

export function buildPayload(s: StoredConsent): ConsentApiPayload {
  return {
    consent_id: s.id,
    categories: { necessary: true, analytics: s.choices.analytics, marketing: s.choices.marketing },
    action: s.action,
    consent_version: s.v,
    policy_version: s.pv,
    timestamp: s.ts,
    source: s.source,
    page: (typeof location !== 'undefined' && location.pathname) || '/',
    locale: currentLocale(),
    gpc: s.gpc,
  }
}

export interface PostResult {
  ok: boolean
  status?: number
  id?: string
  received_at?: string
  errors?: Record<string, unknown>
}

interface Pending {
  payload: ConsentApiPayload
  attempts: number
  next: number
}
export const MAX_ATTEMPTS = 6

function readPending(): Pending | null {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || 'null')
  } catch {
    return null
  }
}
function writePending(p: Pending | null) {
  try {
    if (p) localStorage.setItem(PENDING_KEY, JSON.stringify(p))
    else localStorage.removeItem(PENDING_KEY)
  } catch {}
}

export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|; )csrftoken=([^;]*)/)
  return m ? decodeURIComponent(m[1]) : null
}

/** csrftoken cookie; if missing, GET the endpoint (which sets it) and read again. */
export async function ensureCsrfToken(apiUrl: string): Promise<string | null> {
  let t = readCsrfCookie()
  if (t) return t
  try {
    await fetch(apiUrl, { method: 'GET', credentials: 'same-origin' })
  } catch {}
  t = readCsrfCookie()
  return t || (typeof window !== 'undefined' ? (window as any).csrfTOKEN || null : null)
}

/** Retry-After as seconds or HTTP-date -> epoch ms, or null. */
export function parseRetryAfter(v: string | null | undefined, now = Date.now()): number | null {
  if (!v) return null
  const n = Number(v)
  if (Number.isFinite(n)) return now + Math.max(0, n) * 1000
  const d = Date.parse(v)
  return isNaN(d) ? null : d
}

async function send(
  payload: ConsentApiPayload,
  extraHeaders: Record<string, string> = {}
): Promise<PostResult & { retryAt?: number | null }> {
  const { apiUrl } = getConfig()
  if (!apiUrl || typeof fetch !== 'function') return { ok: false }
  try {
    const csrf = await ensureCsrfToken(apiUrl)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Consent-Id': payload.consent_id,
      ...extraHeaders,
    }
    if (csrf) headers['X-CSRFToken'] = csrf
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      keepalive: true,
    })
    let body: any = null
    try {
      body = await res.json()
    } catch {}
    if (res.ok) return { ok: true, status: res.status, id: body?.id, received_at: body?.received_at }
    if (res.status === 400) return { ok: false, status: 400, errors: body || {} }
    if (res.status === 429) return { ok: false, status: 429, retryAt: parseRetryAfter(res.headers?.get?.('Retry-After')) }
    return { ok: false, status: res.status }
  } catch {
    return { ok: false }
  }
}

function handleFailure(payload: ConsentApiPayload, attempts: number, r: { status?: number; retryAt?: number | null }) {
  // 400 = our payload is invalid; retrying would just repeat it.
  if (r.status === 400 || attempts >= MAX_ATTEMPTS) return writePending(null)
  const backoff = Date.now() + 60_000 * 2 ** (attempts - 1) // 1, 2, 4, 8, 16 min
  writePending({ payload, attempts, next: Math.max(backoff, r.retryAt || 0) })
}

export async function postConsent(s: StoredConsent, extraHeaders?: Record<string, string>): Promise<PostResult> {
  if (!getConfig().apiUrl) return { ok: false }
  const payload = buildPayload(s)
  // Queue first: if the page reloads (withdrawal) or closes mid-request, the
  // receipt is retried on the next page load.
  writePending({ payload, attempts: 0, next: 0 })
  const r = await send(payload, extraHeaders)
  if (r.ok) writePending(null)
  else handleFailure(payload, 1, r)
  const { retryAt, ...out } = r
  return out
}

/** Call once per page load: retries a queued receipt when its backoff/Retry-After has elapsed. */
export async function retryPendingConsent(now = Date.now()): Promise<boolean> {
  const p = readPending()
  if (!p || !getConfig().apiUrl || now < p.next) return false
  const r = await send(p.payload)
  if (r.ok) writePending(null)
  else handleFailure(p.payload, p.attempts + 1, r)
  return r.ok
}

export interface ServerConsent {
  id: string
  consent_id: string
  categories: { necessary: boolean; analytics: boolean; marketing: boolean }
  action: ConsentAction
  consent_version: number
  policy_version: string
  received_at: string
}

/** GET the current server state (signed-in user via authHeaders, else by consent_id). */
export async function fetchServerConsent(authHeaders: Record<string, string> = {}): Promise<ServerConsent | null> {
  const { apiUrl } = getConfig()
  if (!apiUrl || typeof fetch !== 'function') return null
  try {
    const id = getConsentId()
    const res = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(id ? { 'X-Consent-Id': id } : {}), ...authHeaders },
    })
    if (!res.ok) return null
    const body = await res.json()
    return body?.current ?? null
  } catch {
    return null
  }
}

export type SyncOutcome = 'none' | 'pushed_local' | 'adopted_server' | 'in_sync' | 'error'

/**
 * Login merge. Call right after a successful login with the new auth headers
 * (the login request itself already sent X-Consent-Id, so the backend has
 * attached this browser's anonymous rows to the account).
 *  - server has nothing, local has a choice      -> POST local (now linked to the user)
 *  - server newer than local (or local missing)  -> adopt server state locally (no re-post)
 *  - local newer than server                     -> POST local
 *  - same consent_id and same categories         -> nothing
 * A server state from an older consent_version is ignored (the user is re-prompted).
 */
export async function syncConsentAfterLogin(authHeaders: Record<string, string> = {}): Promise<SyncOutcome> {
  try {
    const local = getConsent() || readStoredConsent()
    const server = await fetchServerConsent(authHeaders)
    const cfg = getConfig()
    const serverUsable = !!server && server.consent_version === cfg.version
    if (!serverUsable) {
      if (!local) return 'none'
      const r = await postConsent(local, authHeaders)
      return r.ok ? 'pushed_local' : 'error'
    }
    const sTime = Date.parse(server!.received_at)
    const same =
      !!local &&
      local.choices.analytics === server!.categories.analytics &&
      local.choices.marketing === server!.categories.marketing
    if (same) return 'in_sync'
    if (!local || (!isNaN(sTime) && sTime > Date.parse(local.ts))) {
      applyServerConsent(server!)
      return 'adopted_server'
    }
    const r = await postConsent(local, authHeaders)
    return r.ok ? 'pushed_local' : 'error'
  } catch {
    return 'error'
  }
}
